import { IconButton, Tooltip } from "@mui/material";

import { useContext, useState } from "react";
import TranscriptRequestDialog from "./TranscriptRequestDialog";
import { useTranslation } from "react-i18next";
import { useTranscriptRequest } from "../../hooks/useTranscription";
import { useAlert } from "../../context/AlertContext";
import { AuthContext } from "../../context/AuthContext";
import { PrivacyTip, ArticleOutlined } from "@mui/icons-material";

interface TranscriptionActionButtonProps {
  customerId: string;
  interactionId: string;
  hasExistingTranscript?: boolean;
  onTranscriptionStarted?: (jobId: string) => void;
}

export function TranscriptionActionButton({
  customerId,
  interactionId,
  hasExistingTranscript = false,
  onTranscriptionStarted,
}: Readonly<TranscriptionActionButtonProps>) {
  const { mutateAsync: transcribeAsync } = useTranscriptRequest();
  const showAlert = useAlert();
  const { user } = useContext(AuthContext);
  const { t } = useTranslation(["transcription"]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleRequestTranscript = async () => {
    if (!userHasPermissionToRedact()) {
      const result = await transcribeAsync({
        customerId,
        interactionId,
      });
      onTranscriptionStarted?.(result.jobId);
      showAlert(t("transcription:transcriptRequestSubmitted"), "success");
      return;
    }
    setIsDialogOpen(true);
  };

  function userHasPermissionToRedact(): boolean {
    return user?.hasPermission("interactions:Redact") ?? false;
  }

  // if transcript already exists, and no permissions for redaction, hide completely; no action to be taken
  const shouldHide = !userHasPermissionToRedact() && hasExistingTranscript;

  if (shouldHide) return null;

  return (
    <>
      <Tooltip
        title={
          hasExistingTranscript
            ? t("transcription:requestRedaction")
            : t("transcription:requestTranscript")
        }
      >
        <IconButton onClick={handleRequestTranscript} size="small">
          {hasExistingTranscript ? <PrivacyTip /> : <ArticleOutlined />}
        </IconButton>
      </Tooltip>

      <TranscriptRequestDialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        customerId={customerId}
        interactionId={interactionId}
        hasExistingTranscript={hasExistingTranscript}
        onTranscriptionStarted={onTranscriptionStarted}
      />
    </>
  );
}
